/* ============================================
   INCIPIT — HERO STORIES (JS)
   Auto-play, progress ring sync, hover pause,
   tab visibility pause, smooth scroll-to-next.
   Code original Incipit Studio.
   ============================================ */

(function () {
  'use strict';

  /* ----- Constants ----- */
  var REVEAL_FALLBACK_MS = 800;
  var SCROLL_DURATION_MS = 1600;
  var RING_LENGTH = 100;          // matches CSS `pathLength="100"`
  var DEFAULT_DURATION_S = 6;

  /* ----- Shared state ----- */
  var isScrolling = false;
  var animFrameId = null;


  /* =========================================
     SMOOTH SCROLL (premium easing)
     ========================================= */

  function easeInOutCubic(t) {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function customSmoothScrollTo(targetY, duration) {
    if (animFrameId) {
      cancelAnimationFrame(animFrameId);
      animFrameId = null;
    }

    var startY = window.scrollY;
    var distance = targetY - startY;
    var startTime = performance.now();

    if (Math.abs(distance) < 50) {
      window.scrollTo(0, targetY);
      isScrolling = false;
      return;
    }

    isScrolling = true;

    function step(currentTime) {
      var elapsed = currentTime - startTime;
      var progress = Math.min(elapsed / duration, 1);
      var eased = easeInOutCubic(progress);
      window.scrollTo(0, startY + distance * eased);
      if (progress < 1) {
        animFrameId = requestAnimationFrame(step);
      } else {
        animFrameId = null;
        isScrolling = false;
      }
    }

    animFrameId = requestAnimationFrame(step);
  }

  /* Header used during scroll: only fixed/sticky take space at runtime */
  function getStickyHeaderHeight() {
    var selectors = [
      'header[role="banner"]',
      '.section-header',
      '#shopify-section-header',
      '.header',
      'header.sticky',
      'header'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var header = document.querySelector(selectors[i]);
      if (header) {
        var pos = window.getComputedStyle(header).position;
        if (pos === 'fixed' || pos === 'sticky') {
          return header.offsetHeight;
        }
        return 0;
      }
    }
    return 0;
  }

  function smoothScrollToNext(wrapper) {
    var section = wrapper.closest('.shopify-section');
    if (!section) {
      customSmoothScrollTo(window.scrollY + window.innerHeight, SCROLL_DURATION_MS);
      return;
    }

    var nextSection = section.nextElementSibling;
    while (nextSection && (
      nextSection.tagName === 'SCRIPT' ||
      nextSection.tagName === 'STYLE' ||
      nextSection.offsetHeight === 0
    )) {
      nextSection = nextSection.nextElementSibling;
    }

    if (!nextSection) {
      customSmoothScrollTo(window.scrollY + window.innerHeight, SCROLL_DURATION_MS);
      return;
    }

    var targetY = nextSection.getBoundingClientRect().top + window.scrollY - getStickyHeaderHeight();
    customSmoothScrollTo(Math.max(0, targetY), SCROLL_DURATION_MS);
  }


  /* =========================================
     FULL-BLEED OFFSET
     Slides the section under in-flow headers
     (static / relative / sticky). Skipped for
     fixed / absolute headers since those are
     already overlaying content.
     ========================================= */

  function applyFullBleedOffset(wrapper) {
    if (wrapper.dataset.fullBleed !== 'true') return;

    var section = wrapper.closest('.shopify-section');
    if (!section) return;

    /* Add the conventional `header-is-transparent` hook so themes that
       support it (Release, Impulse, Symmetry, etc.) automatically
       switch their header to transparent + absolute positioning. */
    section.classList.add('header-is-transparent');
    document.body.classList.add('header-is-transparent');

    /* Force a reflow so the theme's CSS reaction (if any) applies
       before we measure the header's computed position. */
    void document.body.offsetHeight;

    var selectors = [
      'header[role="banner"]',
      '.section-header',
      '#shopify-section-header',
      '.header'
    ];

    var header = null;
    for (var i = 0; i < selectors.length; i++) {
      var found = document.querySelector(selectors[i]);
      if (found) { header = found; break; }
    }

    if (!header) {
      section.style.marginTop = '';
      return;
    }

    var pos = window.getComputedStyle(header).position;
    /* If theme reacted by making the header out-of-flow, no margin
       needed — it's already overlaying our section. */
    if (pos === 'fixed' || pos === 'absolute') {
      section.style.marginTop = '';
    } else {
      /* Header is still in-flow — pull our section under it. */
      section.style.marginTop = '-' + header.offsetHeight + 'px';
    }
  }


  /* =========================================
     HERO STORIES INSTANCE
     ========================================= */

  function HeroStories(wrapper) {
    var hero = wrapper.querySelector('[data-incipit-hs]');
    if (!hero) return;

    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-incipit-hs-slide]'));
    var indicators = Array.prototype.slice.call(hero.querySelectorAll('[data-incipit-hs-indicator]'));
    var scrollBtn = wrapper.querySelector('[data-incipit-hs-scroll]');

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var duration = (parseFloat(hero.dataset.autoplayDuration) || DEFAULT_DURATION_S) * 1000;
    var pauseOnHover = hero.dataset.pauseOnHover === 'true';

    var currentIndex = 0;
    var timer = null;
    var isPaused = false;
    var isHidden = false;


    /* ----- Reveal ----- */
    function reveal() {
      requestAnimationFrame(function () {
        hero.classList.add('is-revealed');
      });
    }


    /* ----- Force reflow (used to reset CSS transitions) ----- */
    function forceReflow(el) {
      // eslint-disable-next-line no-unused-expressions
      el.getBoundingClientRect();
    }


    /* ----- Show slide ----- */
    function showSlide(targetIndex, opts) {
      opts = opts || {};
      var animate = opts.animate !== false;

      // Wrap around
      if (targetIndex < 0) targetIndex = slides.length - 1;
      if (targetIndex >= slides.length) targetIndex = 0;

      var oldSlide = slides[currentIndex];
      var newSlide = slides[targetIndex];
      var isChanging = oldSlide !== newSlide;

      // Mark old slide as leaving — triggers the reverse cascade text fade-out.
      // Class is removed after the longest cascade transition completes
      // (0.54s delay + 0.6s duration = ~1.14s, rounded up for safety).
      if (isChanging && animate && oldSlide) {
        oldSlide.classList.add('is-leaving');
        setTimeout(function () {
          oldSlide.classList.remove('is-leaving');
        }, 1300);
      }

      // Activate slides
      slides.forEach(function (slide, i) {
        if (i === targetIndex) {
          slide.classList.add('is-active');
          slide.removeAttribute('aria-hidden');
          // Trigger video play when becoming active
          var video = slide.querySelector('video');
          if (video && video.paused) {
            var playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(function () { /* autoplay may be blocked, silent */ });
            }
          }
        } else {
          slide.classList.remove('is-active');
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      // Sync indicators + reset progress rings
      indicators.forEach(function (indicator, i) {
        var ring = indicator.querySelector('.incipit-hs__indicator-ring');
        if (i === targetIndex) {
          indicator.classList.add('is-active');
          indicator.setAttribute('aria-selected', 'true');
          if (ring) {
            // Reset ring to empty, then animate to full over `duration`
            ring.style.transition = 'none';
            ring.style.strokeDashoffset = RING_LENGTH;
            forceReflow(ring);
            if (animate && !reducedMotion) {
              ring.style.transition = 'stroke-dashoffset ' + duration + 'ms linear';
              ring.style.strokeDashoffset = '0';
            }
          }
        } else {
          indicator.classList.remove('is-active');
          indicator.removeAttribute('aria-selected');
          if (ring) {
            ring.style.transition = 'none';
            ring.style.strokeDashoffset = RING_LENGTH;
          }
        }
      });

      currentIndex = targetIndex;
    }


    /* ----- Auto-play timer ----- */
    function startTimer(customDelay) {
      if (slides.length <= 1) return;
      if (reducedMotion) return;
      stopTimer();
      var delay = typeof customDelay === 'number' ? customDelay : duration;
      timer = setTimeout(function () {
        if (!isPaused && !isHidden) {
          showSlide(currentIndex + 1);
          startTimer();
        }
      }, delay);
    }

    function stopTimer() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }


    /* ----- Pause / Resume (precise, no visual jump) ----- */
    function pauseAutoplay() {
      if (isPaused) return;
      isPaused = true;
      stopTimer();
      // Freeze active ring at its current position
      var activeRing = hero.querySelector('.incipit-hs__indicator.is-active .incipit-hs__indicator-ring');
      if (activeRing) {
        var current = window.getComputedStyle(activeRing).strokeDashoffset;
        activeRing.style.transition = 'none';
        activeRing.style.strokeDashoffset = current;
      }
    }

    function resumeAutoplay() {
      if (!isPaused) return;
      isPaused = false;
      var activeRing = hero.querySelector('.incipit-hs__indicator.is-active .incipit-hs__indicator-ring');
      if (activeRing && !reducedMotion) {
        var currentOffset = parseFloat(window.getComputedStyle(activeRing).strokeDashoffset) || 0;
        var remainingRatio = currentOffset / RING_LENGTH;
        var remainingMs = Math.max(50, duration * remainingRatio);
        forceReflow(activeRing);
        activeRing.style.transition = 'stroke-dashoffset ' + remainingMs + 'ms linear';
        activeRing.style.strokeDashoffset = '0';
        startTimer(remainingMs);
      } else {
        startTimer();
      }
    }


    /* ----- Indicator clicks ----- */
    indicators.forEach(function (indicator, i) {
      indicator.addEventListener('click', function () {
        if (i === currentIndex) return;
        showSlide(i);
        if (!isPaused && !isHidden) startTimer();
      });
    });


    /* ----- Hover pause ----- */
    if (pauseOnHover) {
      hero.addEventListener('mouseenter', pauseAutoplay);
      hero.addEventListener('mouseleave', resumeAutoplay);
    }


    /* ----- Tab visibility ----- */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        isHidden = true;
        pauseAutoplay();
      } else {
        isHidden = false;
        resumeAutoplay();
      }
    });


    /* ----- Reveal trigger (waits for first slide media) ----- */
    var firstSlide = slides[0];
    var firstMedia = firstSlide ? firstSlide.querySelector('.incipit-hs__picture img, video') : null;

    if (reducedMotion) {
      reveal();
    } else if (!firstMedia) {
      reveal();
    } else if (firstMedia.tagName === 'VIDEO' || firstMedia.complete) {
      reveal();
    } else {
      firstMedia.addEventListener('load', reveal, { once: true });
      setTimeout(reveal, REVEAL_FALLBACK_MS);
    }


    /* ----- Initial state + start ----- */
    showSlide(0, { animate: !reducedMotion });
    if (!reducedMotion && slides.length > 1) startTimer();


    /* ----- Scroll button ----- */
    if (scrollBtn) {
      scrollBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (isScrolling) return;
        if (reducedMotion) {
          var sectionWrapper = wrapper.closest('.shopify-section');
          var nextSection = sectionWrapper ? sectionWrapper.nextElementSibling : null;
          if (nextSection) {
            window.scrollTo(0, nextSection.getBoundingClientRect().top + window.scrollY - 80);
          }
          return;
        }
        smoothScrollToNext(wrapper);
      });
    }


    /* ----- Full-bleed: slide section under header ----- */
    applyFullBleedOffset(wrapper);

    var resizeTimeout;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        applyFullBleedOffset(wrapper);
      }, 150);
    });
  }


  /* =========================================
     INIT
     ========================================= */

  function init() {
    var wrappers = document.querySelectorAll('[data-incipit-hs-wrapper]');
    wrappers.forEach(function (wrapper) {
      HeroStories(wrapper);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor support: re-init on section load
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var wrapper = e.target.querySelector('[data-incipit-hs-wrapper]');
      if (wrapper) HeroStories(wrapper);
    });
  }
})();